/**
 * PuzzleEditor (Management Console) — top-level WYSIWYG puzzle editor page.
 *
 * Two-column layout:
 *   Left: Puzzle list panel with filter/sort
 *   Right: 75%-scaled board + alternates + clue layout controls
 *
 * Toolbars split: New/Schedule above list, Save/Import/Export above board.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useEditorState } from './useEditorState';
import { ensureFontReady } from '../../utils/textFitting';
import { ensureBarlowReady } from '../../utils/phraseMaker';
import EditorToolbar from './EditorToolbar';
import FrontPanel from './FrontPanel';
import PuzzleList from './PuzzleList';
import type { PuzzleListHandle } from './PuzzleList';
import AlternatesSection from './AlternatesSection';
import DisplayHintControls from './DisplayHintControls';
import ContentRunwayIndicator from './ContentRunwayIndicator';
import MiniLogoBanner from './MiniLogoBanner';
import { confirmUnsaved } from './confirmUnsaved';
import styles from './PuzzleEditor.module.css';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/** Scale factor for the board panel — preserves WYSIWYG rendering */
const BOARD_SCALE = 0.75;
/** Original board panel width (930px) — do not change, internal layout depends on this */
const BOARD_FULL_W = 930;
const BOARD_SCALED_W = Math.round(BOARD_FULL_W * BOARD_SCALE);

/** Editor design width: leftCol(340) + gap(16) + rightCol(698) + padding(64) */
const EDITOR_W = 1118;
/** Minimum visual border around the console (left, right, bottom) */
const EDGE_PAD = 16;
/** Scale clamping: visual height stays between these bounds */
const MIN_VISUAL_H = 600;
const MAX_VISUAL_H = 2000;

/** Derive puzzle status for the PLAY button */
function derivePuzzleStatus(puzzle: { solution: { primary: string }; columns: Array<{ category: string; pairs: Array<{ clues: [string, string] }> }> }, date: string | null): 'scheduled' | 'ready' | 'core' | 'stub' {
  if (!puzzle.solution?.primary) return 'stub';
  const allClues = puzzle.columns.every(col =>
    col.pairs.every(pair => pair.clues[0].trim() !== '' && pair.clues[1].trim() !== '')
  );
  if (!allClues) return 'stub';
  const allCategories = puzzle.columns.every(col => col.category?.trim() !== '');
  if (!allCategories) return 'core';
  return date ? 'scheduled' : 'ready';
}

const PuzzleEditor: React.FC = () => {
  const [state, actions] = useEditorState();
  const [focusedCell, setFocusedCell] = useState<{ col: number; row: number } | null>(null);
  const [fontLoaded, setFontLoaded] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [scheduledDates, setScheduledDates] = useState<Set<string> | null>(null);

  // Viewport size tracking for dynamic scaling
  const [vpSize, setVpSize] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setVpSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Refs for measuring editor and board panel heights
  const editorRef = useRef<HTMLDivElement>(null);
  const boardInnerRef = useRef<HTMLDivElement>(null);
  const [naturalH, setNaturalH] = useState(900);
  const [boardH, setBoardH] = useState(800);

  useEffect(() => {
    Promise.all([ensureFontReady(), ensureBarlowReady()])
      .then(() => setFontLoaded(true));
  }, []);

  // Fetch scheduled dates (lifted from ContentRunwayIndicator)
  useEffect(() => {
    fetch(`${supabaseUrl}/rest/v1/puzzles_admin?date=not.is.null&select=date&limit=500`, {
      headers: {
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`,
      },
    })
      .then(res => res.json())
      .then((rows: Array<{ date: string }>) => {
        setScheduledDates(new Set(rows.map(r => r.date)));
      })
      .catch(() => setScheduledDates(new Set()));
  }, [refreshKey]);

  // Measure editor and board panel heights for layout
  useEffect(() => {
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        if (e.target === editorRef.current) {
          const bbs = e.borderBoxSize?.[0];
          if (bbs) setNaturalH(bbs.blockSize);
        }
        if (e.target === boardInnerRef.current) setBoardH(e.contentRect.height);
      }
    });
    if (editorRef.current) ro.observe(editorRef.current);
    if (boardInnerRef.current) ro.observe(boardInnerRef.current);
    return () => ro.disconnect();
  }, []);

  const puzzleStatus = useMemo(
    () => derivePuzzleStatus(state.puzzle, state.activePuzzleDate),
    [state.puzzle, state.activePuzzleDate]
  );

  // Dynamic scaling: fit both dimensions with minimum border
  const availW = vpSize.w - EDGE_PAD * 2;
  const availH = vpSize.h - EDGE_PAD * 2;
  const wScale = EDITOR_W > 0 ? availW / EDITOR_W : 1;
  const hScale = naturalH > 0 ? availH / naturalH : 1;
  const fitScale = Math.min(wScale, hScale);
  const minScale = naturalH > 0 ? MIN_VISUAL_H / naturalH : 0.5;
  const maxScale = naturalH > 0 ? MAX_VISUAL_H / naturalH : 3;
  const editorScale = Math.max(minScale, Math.min(maxScale, fitScale));
  const scaledW = EDITOR_W * editorScale;
  const scaledH = naturalH * editorScale;
  const centerMarginLeft = Math.max(0, (vpSize.w - scaledW) / 2);
  const centerMarginTop = Math.max(0, (vpSize.h - scaledH) / 2);

  const handleListRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const handleSampleToggled = useCallback((id: string, isSample: boolean) => {
    if (id === state.activePuzzleId) {
      actions.setIsSample(isSample);
    }
  }, [state.activePuzzleId, actions]);

  const puzzleListRef = useRef<PuzzleListHandle>(null);

  const handleCompressDates = useCallback(async () => {
    await puzzleListRef.current?.compressDates();
    setRefreshKey(k => k + 1);
  }, []);

  const autoSelectedRef = useRef(false);
  const handleAutoSelect = useCallback((id: string) => {
    if (autoSelectedRef.current) return;
    autoSelectedRef.current = true;
    actions.loadFromSupabase(id);
  }, [actions]);

  const handleSelectPuzzle = useCallback(async (id: string) => {
    if (id === state.activePuzzleId) return;
    if (state.isDirty) {
      const choice = await confirmUnsaved('You have unsaved changes.');
      if (choice === 'cancel') return;
      if (choice === 'save') {
        const saved = await actions.saveToSupabase();
        if (!saved) return;
        setRefreshKey(k => k + 1);
      }
    }
    await actions.loadFromSupabase(id);
  }, [state.activePuzzleId, state.isDirty, actions]);

  return (
    <div className={styles.viewportWrap}>
      <EditorToolbar state={state} actions={actions} onSaved={handleListRefresh} puzzleStatus={puzzleStatus} onCompressDates={handleCompressDates}>
        {({ listToolbar, boardToolbar }) => (
          <div
            ref={editorRef}
            className={styles.editor}
            style={{
              transform: `scale(${editorScale})`,
              transformOrigin: 'top left',
              marginLeft: centerMarginLeft,
              marginTop: centerMarginTop,
            }}
          >
            {/* Header */}
            <div className={styles.headerRow}>
              <MiniLogoBanner />
              <span className={styles.headerTitle}>Management Console</span>
            </div>

            {/* Two-column body */}
            <div className={styles.columns}>
              {/* ─── Left column: toolbar + puzzle list + runway indicator ─── */}
              <div className={styles.leftCol}>
                <div className={styles.leftColInner}>
                  {listToolbar}
                  <PuzzleList
                    ref={puzzleListRef}
                    selectedId={state.activePuzzleId}
                    onSelect={handleSelectPuzzle}
                    refreshKey={refreshKey}
                    onSampleToggled={handleSampleToggled}
                    onFirstItemReady={handleAutoSelect}
                  />
                  <div style={{ marginTop: 12, flexShrink: 0 }}>
                    <div className={styles.regionBox} style={{ maxWidth: 'none' }}>
                      <span className={styles.regionLabel} style={{ fontSize: 12 }}>Puzzle Calendar</span>
                      <ContentRunwayIndicator scheduledDates={scheduledDates} />
                    </div>
                  </div>
                </div>
              </div>

              {/* ─── Right column: toolbar + scaled board + extras ─── */}
              <div className={styles.rightCol}>
                {boardToolbar}

                {/* Board area */}
                <div style={{ position: 'relative' }}>
                  {/* Scale wrapper — CSS transform preserves WYSIWYG rendering */}
                  <div
                    className={styles.scaleWrap}
                    style={{
                      width: BOARD_SCALED_W,
                      height: Math.ceil(boardH * BOARD_SCALE),
                    }}
                  >
                    <div
                      ref={boardInnerRef}
                      className={styles.scaleInner}
                      style={{
                        width: BOARD_FULL_W,
                        transform: `scale(${BOARD_SCALE})`,
                        transformOrigin: 'top left',
                      }}
                    >
                      {/* Board panel — single view with categories + clues */}
                      <div className={styles.boardPanel}>
                        <span className={styles.regionLabel}>Game Board</span>
                        <div className={styles.flipInnerBox}>
                          <FrontPanel
                            puzzle={state.puzzle}
                            actions={actions}
                            onCellFocus={(col, row) => setFocusedCell({ col, row })}
                          />
                        </div>
                      </div>

                      <div style={{ marginTop: 16 }} className={styles.regionBox}>
                        <span className={styles.regionLabel}>Alternate Answers</span>
                        <AlternatesSection puzzle={state.puzzle} actions={actions} />
                      </div>

                      <div style={{ marginTop: 16 }} className={styles.regionBox}>
                        <DisplayHintControls
                          puzzle={state.puzzle}
                          actions={actions}
                          focusedCell={focusedCell}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!fontLoaded && (
              <div style={{
                position: 'fixed', bottom: 12, right: 12,
                background: '#514b44', borderRadius: 6, padding: '6px 12px',
                fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#f7e5c5', opacity: 0.7,
              }}>
                Loading font...
              </div>
            )}
          </div>
        )}
      </EditorToolbar>
    </div>
  );
};

export default PuzzleEditor;
